import React from 'react';
import styles from './Tabs.module.css';

/**
 * Underline-style tab bar. Renders a horizontal list of tabs with a moving
 * underline indicator beneath the selected tab and a full-width divider line.
 *
 * @component
 * @param {Object} props
 * @param {string|number} props.value - The value of the currently selected tab.
 * @param {(value: string|number) => void} props.onChange - Called with the new value when a tab is clicked.
 * @param {Array<{ value: string|number, label: import('react').ReactNode, icon?: import('react').ReactNode, disabled?: boolean }>} props.tabs - The tabs to render.
 * @param {string} [props.className] - Optional class applied to the tab bar container.
 */
const Tabs = ({ value, onChange, tabs = [], className, ...rest }) => {
  return (
    <div
      className={[styles.tabs, className].filter(Boolean).join(' ')}
      role="tablist"
      {...rest}
    >
      {tabs.map((tab) => {
        const selected = tab.value === value;
        const tabClassName = [styles.tab, selected && styles.tabSelected]
          .filter(Boolean)
          .join(' ');
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={tab.disabled}
            className={tabClassName}
            onClick={() => {
              if (!tab.disabled && !selected) onChange?.(tab.value);
            }}
          >
            {tab.icon && <span className={styles.tabIcon}>{tab.icon}</span>}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
